import axios from "axios";
import {
    CoverOptions,
    SpiralItem,
    RubberBandItem,
    ExtraPageResource,
    ImageDetails,
    CustomImage,
    BurdeSectionResponse,
    BurdeSingleSectionResponse,
    BurdeFontResponse,
    BurdeFontResource,
} from "burde";
import { LegacyFonts } from "svg/constants";
import {
    LayoutApiResponse,
    LayoutFont,
    SvgCalendarLayout,
    ModuleResource,
    ModuleOption,
} from "ui/constants";
import * as api from "util/api";

const getSpiralOptionsBySize = async (calendarSize: string) => {
    try {
        const { success, data } = await api.json.get(
            "api/ui/GetSpiralOptionsBySize",
            {
                calendarSize,
            },
        );

        if (!success) {
            return [];
        }

        return data as Array<SpiralItem>;
    } catch {
        return [];
    }
};

const getRubberBandOptionsBySize = async (calendarSize: string) => {
    try {
        const { success, data } = await api.json.get(
            "api/ui/GetRubberBandOptionsBySize",
            {
                calendarSize,
            },
        );

        if (!success) {
            return [];
        }

        return data as Array<RubberBandItem>;
    } catch {
        return [];
    }
};

const getCoverOptions = async (culture: string, calendarSize: string) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetCoverOptions`, {
            culture,
            calendarSize,
        });

        if (!success) {
            return null;
        }

        return data as CoverOptions;
    } catch (e) {
        return null;
    }
};

const getLayoutOptions = async (
    culture: string,
    calendarSize: string,
    calendarType: string,
) => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetLayoutOptions`,
            {
                culture,
                calendarSize,
                calendarType,
            },
        );

        if (!success) {
            return null;
        }

        return data as LayoutApiResponse;
    } catch (e) {
        return null;
    }
};

const getLayoutById = async (id: number, culture: string | null) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetLayoutById`, {
            id,
            culture,
        });

        if (!success) {
            return null;
        }

        const layout = data as SvgCalendarLayout;
        return layout;
    } catch (e) {
        return null;
    }
};

const getBorderOptions = async (culture: string, calendarSize: string) => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetBorderOptions`,
            {
                culture,
                calendarSize,
            },
        );

        if (!success) {
            return null;
        }

        return data;
    } catch (e) {
        return null;
    }
};

const getDayModules = async (
    culture: string,
    calendarSize: string,
    calendarType: string,
) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetDayModules`, {
            culture,
            calendarSize,
            calendarType,
        });

        if (!success) {
            return null;
        }

        return data as Array<ModuleResource>;
    } catch (e) {
        return null;
    }
};

const getDayModuleById = async (
    culture: string,
    id: number,
): Promise<ModuleOption | null> => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetDayModuleById`,
            {
                culture,
                id,
            },
        );

        if (!success) {
            return null;
        }

        return data;
    } catch (e) {
        return null;
    }
};

const getDayModulesByIds = async (
    culture: string,
    ids: number[],
): Promise<ModuleOption[] | null> => {
    try {
        const { success, data } = await api.json.post(
            `api/ui/GetDayModulesByIds?culture=${culture}`,
            {
                dayModuleIds: ids,
            },
        );

        if (!success) {
            return null;
        }

        return data;
    } catch (e) {
        return null;
    }
};

const getWeekModuleById = async (
    culture: string,
    id: number,
): Promise<ModuleOption | null> => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetWeekModuleById`,
            {
                culture,
                id,
            },
        );

        if (!success) {
            return null;
        }

        return data;
    } catch (e) {
        return null;
    }
};

const getWeekModulesByIds = async (
    culture: string,
    ids: number[],
): Promise<ModuleOption[] | null> => {
    try {
        const { success, data } = await api.json.post(
            `api/ui/GetWeekModulesByIds?culture=${culture}`,
            {
                weekModuleIds: ids,
            },
        );

        if (!success) {
            return null;
        }

        return data;
    } catch (e) {
        return null;
    }
};

const getWeekModules = async (
    culture: string,
    calendarSize: string,
    calendarType: string,
) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetWeekModules`, {
            culture,
            calendarSize,
            calendarType,
        });

        if (!success) {
            return null;
        }

        return data as Array<ModuleResource>;
    } catch (e) {
        return null;
    }
};

const getExtraPagesByIds = async (
    culture: string,
    calendarSize: string,
    ids: number[],
): Promise<ExtraPageResource[] | null> => {
    const { success, data } = await api.json.post(`api/ui/GetExtraPagesByIds`, {
        culture,
        calendarSize,
        extraPageResourceIds: ids,
    });

    if (!success) return null;

    return data;
};

const getOwnerPageImageOptions = async (
    culture: string,
    calendarSize: string,
) => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetOwnerPageImageOptions`,
            {
                culture,
                calendarSize,
            },
        );

        if (!success) {
            return null;
        }

        return data as CoverOptions;
    } catch (e) {
        return null;
    }
};

const getOwnerPageImageById = async (id: number, culture: string) => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetOwnerPageImageById`,
            {
                id,
                culture,
            },
        );

        if (!success) {
            return null;
        }

        return data as ImageDetails;
    } catch (e) {
        return null;
    }
};

export const uploadCoverImage = async (
    blob: Blob,
    calendarSize: string,
): Promise<CustomImage | null> => {
    try {
        const config = {
            headers: { "content-type": "multipart/form-data" },
        };

        const data = new FormData();
        data.append("File", blob);
        data.append("calendarSize", calendarSize);

        const response = await axios.post(
            "api/ImageUpload/UploadCoverImage",
            data,
            config,
        );

        if (response.status !== 200) {
            return null;
        }

        return response.data;
    } catch (e) {
        return null;
    }
};

export const getCoverSections = async (
    culture: string,
    calendarSize: string,
    calendarType: string,
) => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetCoverSections`,
            {
                culture,
                calendarSize,
                calendarType,
            },
        );

        if (!success) {
            return null;
        }

        return data as BurdeSectionResponse;
    } catch (e) {
        return null;
    }
};

export const getCoverSectionById = async (
    id: number,
    culture: string,
    calendarSize: string,
) => {
    try {
        const { success, data } = await api.json.get(
            `api/ui/GetCoverSectionById`,
            {
                id,
                culture,
                calendarSize,
            },
        );

        if (!success) {
            return null;
        }

        return data as BurdeSingleSectionResponse;
    } catch (e) {
        return null;
    }
};

export const getCoverById = async (id: number, culture: string) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetCoverById`, {
            id,
            culture,
        });

        if (!success) {
            return null;
        }

        return data as ImageDetails;
    } catch (e) {
        return null;
    }
};

export const getBorderById = async (id: number, culture: string) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetBorderById`, {
            id,
            culture,
        });

        if (!success) {
            return null;
        }

        return data as ImageDetails;
    } catch (e) {
        return null;
    }
};

export const getFonts = async (
    culture: string,
    calendarSize: string,
    calendarType: string,
) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetFonts`, {
            culture,
            calendarSize,
            calendarType,
        });

        if (!success) {
            return null;
        }

        return data as BurdeFontResponse;
    } catch (e) {
        return null;
    }
};

export const getFontById = async (id: number, culture: string) => {
    try {
        const { success, data } = await api.json.get(`api/ui/GetFontById`, {
            id,
            culture,
        });

        if (!success) {
            return null;
        }

        return data as BurdeFontResource;
    } catch (e) {
        return null;
    }
};

export {
    getSpiralOptionsBySize,
    getRubberBandOptionsBySize,
    getCoverOptions,
    getLayoutOptions,
    getLayoutById,
    getBorderOptions,
    getDayModules,
    getWeekModules,
    getDayModuleById,
    getDayModulesByIds,
    getWeekModuleById,
    getWeekModulesByIds,
    getExtraPagesByIds,
    getOwnerPageImageOptions,
    getOwnerPageImageById,
};
